import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  User, Mail, School, Calendar, FileText, MessageSquare, Award, 
  ChevronRight, ArrowUp, ArrowDown, ThumbsUp, 
  HelpCircle, MessageCircle, CheckCircle, Flag, MoreHorizontal
} from 'lucide-react';
import AuthContext from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import ProfilePicture from '../../components/ProfilePicture';

// Materials tab content component with pagination
const MaterialsTabContent = ({ userId }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}/materials?page=${pagination.page}&limit=${pagination.limit}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch materials');
        }
        
        const data = await response.json();
        setMaterials(data.materials);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Error fetching materials:', error);
        toast.error('Failed to load materials');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMaterials();
  }, [userId, pagination.page, pagination.limit]);

  if (loading && materials.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading materials...</p>
        </div>
      </div>
    );
  }

  if (materials.length === 0) {
    return (
      <div className="py-8">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No materials</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            This user hasn't uploaded any materials yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {materials.map((material) => (
          <div 
            key={material.id} 
            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
          >
            <Link 
              to={`/materials/${material.id}`}
              className="text-lg font-medium text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400"
            >
              {material.title}
            </Link>
            {material.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                {material.description}
              </p>
            )}
            <div className="flex flex-wrap items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="mr-2">{material.courseCode}: {material.courseName}</span>
              {material.professorName && (
                <>
                  <span className="mx-2">•</span>
                  <span className="mr-2">Prof. {material.professorName}</span>
                </>
              )}
              {material.semester && material.year && (
                <>
                  <span className="mx-2">•</span>
                  <span>{material.semester} {material.year}</span>
                </>
              )}
              <span className="mx-2">•</span>
              <span>{new Date(material.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center mt-3 space-x-4">
              <div className="flex items-center text-green-600 dark:text-green-400">
                <ArrowUp className="h-4 w-4 mr-1" />
                <span className="text-sm">{material.upvotes}</span>
              </div>
              <div className="flex items-center text-red-600 dark:text-red-400">
                <ArrowDown className="h-4 w-4 mr-1" />
                <span className="text-sm">{material.downvotes}</span>
              </div>
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <span className="text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-600">
                  {material.type}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
            disabled={pagination.page === 1}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === 1
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {pagination.page} of {pagination.pages}
          </span>
          
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, prev.pages) }))}
            disabled={pagination.page === pagination.pages}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === pagination.pages
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Comments tab content component with pagination
const CommentsTabContent = ({ userId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}/comments?page=${pagination.page}&limit=${pagination.limit}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch comments');
        }
        
        const data = await response.json();
        setComments(data.comments);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Error fetching comments:', error);
        toast.error('Failed to load comments');
      } finally {
        setLoading(false);
      }
    };
    
    fetchComments();
  }, [userId, pagination.page, pagination.limit]);

  if (loading && comments.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading comments...</p>
        </div>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="py-8">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No comments</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            This user hasn't posted any comments yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {comments.map((comment) => (
          <div 
            key={comment.id} 
            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
          >
            <div className="text-base text-gray-900 dark:text-white">
              {comment.content}
            </div>
            <div className="flex flex-wrap items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
              <Link 
                to={`/materials/${comment.materialId}`}
                className="hover:text-red-600 dark:hover:text-red-400"
              >
                on: {comment.materialTitle}
              </Link>
              <span className="mx-2">•</span>
              <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
              {comment.updatedAt > comment.createdAt && (
                <>
                  <span className="mx-2">•</span>
                  <span className="italic">edited</span>
                </>
              )}
            </div>
            <div className="flex items-center mt-2">
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <ThumbsUp className="h-4 w-4 mr-1" />
                <span className="text-sm">{comment.likes}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
            disabled={pagination.page === 1}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === 1
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {pagination.page} of {pagination.pages}
          </span>
          
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, prev.pages) }))}
            disabled={pagination.page === pagination.pages}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === pagination.pages
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Questions tab content component with pagination
const QuestionsTabContent = ({ userId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}/questions?page=${pagination.page}&limit=${pagination.limit}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch questions');
        }
        
        const data = await response.json();
        setQuestions(data.questions);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Error fetching questions:', error);
        toast.error('Failed to load questions');
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuestions();
  }, [userId, pagination.page, pagination.limit]);

  if (loading && questions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="py-8">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No questions</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            This user hasn't asked any questions yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {questions.map((question) => (
          <div 
            key={question.id} 
            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
          >
            <Link 
              to={`/courses/${question.courseId}/questions/${question.id}`}
              className="text-lg font-medium text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400"
            >
              {question.title}
            </Link>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
              {question.content}
            </p>
            <div className="flex flex-wrap items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="mr-3">{question.courseCode}: {question.courseName}</span>
              <span className="mr-3">{new Date(question.createdAt).toLocaleDateString()}</span>
              <div className="flex items-center space-x-4 ml-auto mt-1 sm:mt-0">
                <span className="flex items-center text-green-600 dark:text-green-400">
                  <ArrowUp className="h-4 w-4 mr-1" />
                  <span>{question.upvotes}</span>
                </span>
                <span className="flex items-center text-red-600 dark:text-red-400">
                  <ArrowDown className="h-4 w-4 mr-1" />
                  <span>{question.downvotes}</span>
                </span>
                <span className="flex items-center text-blue-600 dark:text-blue-400">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  <span>{question.answerCount || 0}</span>
                </span>
                {question.isSolved === 1 && (
                  <span className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span>Solved</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
            disabled={pagination.page === 1}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === 1
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {pagination.page} of {pagination.pages}
          </span>
          
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, prev.pages) }))}
            disabled={pagination.page === pagination.pages}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === pagination.pages
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Answers tab content component with pagination
const AnswersTabContent = ({ userId }) => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    const fetchAnswers = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}/answers?page=${pagination.page}&limit=${pagination.limit}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch answers');
        }
        
        const data = await response.json();
        setAnswers(data.answers);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Error fetching answers:', error);
        toast.error('Failed to load answers');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnswers();
  }, [userId, pagination.page, pagination.limit]);

  if (loading && answers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading answers...</p>
        </div>
      </div>
    );
  }

  if (answers.length === 0) {
    return (
      <div className="py-8">
        <div className="text-center">
          <MessageCircle className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No answers</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            This user hasn't answered any questions yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {answers.map((answer) => (
          <div 
            key={answer.id} 
            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
              <Link 
                to={`/courses/${answer.courseId}/questions/${answer.questionId}`}
                className="text-lg font-medium text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400"
              >
                {answer.questionTitle}
              </Link>
              
              {answer.isAccepted === 1 && (
                <span className="flex items-center mt-2 md:mt-0 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                  <CheckCircle size={14} className="mr-1" />
                  Accepted Answer
                </span>
              )}
            </div>
            
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {answer.courseCode}: {answer.courseName}
            </p>
            
            <div className="mt-3 prose dark:prose-invert max-w-none text-sm">
              <p className="text-gray-700 dark:text-gray-300 line-clamp-3">
                {answer.content}
              </p>
            </div>
            
            <div className="flex items-center mt-3 space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span>{new Date(answer.createdAt).toLocaleDateString()}</span>
              <div className="flex items-center space-x-4 ml-auto">
                <span className="flex items-center text-green-600 dark:text-green-400">
                  <ArrowUp className="h-4 w-4 mr-1" />
                  <span>{answer.upvotes}</span>
                </span>
                <span className="flex items-center text-red-600 dark:text-red-400">
                  <ArrowDown className="h-4 w-4 mr-1" />
                  <span>{answer.downvotes}</span>
                </span>
                {answer.replyCount > 0 && (
                  <span className="flex items-center text-blue-600 dark:text-blue-400">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    <span>{answer.replyCount}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination - identical to Questions tab */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
            disabled={pagination.page === 1}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === 1
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {pagination.page} of {pagination.pages}
          </span>
          
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, prev.pages) }))}
            disabled={pagination.page === pagination.pages}
            className={`px-3 py-1 rounded-md text-sm ${
              pagination.page === pagination.pages
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const PublicProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser } = useContext(AuthContext);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isReporting, setIsReporting] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}/profile`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }
        
        const data = await response.json();
        setProfileData(data);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError(error.message);
        toast.error('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [userId]);

  // Function to get merit level title based on merit score
  const getMeritLevel = (merit) => {
    if (merit >= 1000) return 'Scholar';
    if (merit >= 500) return 'Academic';
    if (merit >= 250) return 'Contributor';
    if (merit >= 100) return 'Student';
    if (merit >= 50) return 'Novice';
    return 'Newcomer';
  };

  // Function to get merit color based on merit score
  const getMeritColor = (merit) => {
    if (merit >= 1000) return 'text-purple-600 dark:text-purple-400';
    if (merit >= 500) return 'text-blue-600 dark:text-blue-400';
    if (merit >= 250) return 'text-green-600 dark:text-green-400';
    if (merit >= 100) return 'text-yellow-600 dark:text-yellow-400';
    if (merit >= 50) return 'text-orange-600 dark:text-orange-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  // Handle reporting users
  const handleReportUser = async () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason for reporting');
      return;
    }
    
    try {
      setIsReporting(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: reportReason.trim() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to report user');
      }
      
      toast.success('User reported successfully');
      setShowReportModal(false);
      setReportReason('');
    } catch (error) {
      console.error('Error reporting user:', error);
      toast.error(error.message || 'Failed to report user');
    } finally {
      setIsReporting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {error || 'User profile not found'}
          </p>
          <Link 
            to="/" 
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {/* Profile header */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center">
            <ProfilePicture url={profileData.profilePictureUrl} size="lg" />
            <div className="mt-4 sm:mt-0 sm:ml-4 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {profileData.name}
                    </h2>
                    {/* ADD REPORT BUTTON - only show if not viewing own profile */}
                    {currentUser && currentUser.id !== userId && (
                      <button
                        onClick={() => setShowReportModal(true)}
                        className="ml-3 p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                        title="Report User"
                      >
                        <Flag size={16} />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Member since {new Date(profileData.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="mt-2 sm:mt-0 flex items-center">
                  <Award className={`h-6 w-6 ${getMeritColor(profileData.merit)} mr-2`} />
                  <div>
                    <p className={`font-medium ${getMeritColor(profileData.merit)}`}>
                      {profileData.merit} Merit
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {getMeritLevel(profileData.merit)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  <School className="h-3 w-3 mr-1" />
                  {profileData.school.name}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 capitalize">
                  <User className="h-3 w-3 mr-1" />
                  {profileData.role}
                </span>
                {profileData.major && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-700 text-blue-800 dark:text-blue-200">
                    <FileText className="h-3 w-3 mr-1" />
                    {profileData.major}
                  </span>
                )}
                {/* Example badge placeholder - in the future, these would be real badges */}
                {profileData.merit >= 100 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-700 text-blue-800 dark:text-blue-200">
                    <Award className="h-3 w-3 mr-1" />
                    Contributor
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Profile navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-3 py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('materials')}
              className={`px-3 py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'materials'
                  ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Materials ({profileData.contentCounts.materials})
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              className={`px-3 py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'questions'
                  ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Questions ({profileData.contentCounts.questions || 0})
            </button>
            <button
              onClick={() => setActiveTab('answers')}
              className={`px-3 py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'answers'
                  ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Answers ({profileData.contentCounts.answers || 0})
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={`px-3 py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'comments'
                  ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Comments ({profileData.contentCounts.comments})
            </button>
            <button
              onClick={() => setActiveTab('badges')}
              className={`px-3 py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'badges'
                  ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Badges (Coming Soon)
            </button>
          </nav>
        </div>
        
        {/* Profile content based on active tab */}
        <div className="px-4 py-5 sm:px-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Bio section */}
              {profileData.bio && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    About
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {profileData.bio}
                  </p>
                </div>
              )}
              {/* Merit breakdown card */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    Merit Breakdown
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {/* Materials Merit */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 flex items-center">
                      <div className="bg-blue-100 dark:bg-blue-700 p-2 rounded-full mr-3">
                        <FileText className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Materials</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {profileData.meritBreakdown?.materialMerit || profileData.materialMerit || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Comments Merit */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 flex items-center">
                      <div className="bg-green-100 dark:bg-green-700 p-2 rounded-full mr-3">
                        <MessageSquare className="h-5 w-5 text-green-600 dark:text-green-300" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Comments</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {profileData.meritBreakdown?.commentMerit || profileData.commentMerit || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Questions Merit */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 flex items-center">
                      <div className="bg-purple-100 dark:bg-purple-700 p-2 rounded-full mr-3">
                        <HelpCircle className="h-5 w-5 text-purple-600 dark:text-purple-300" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Questions</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {profileData.meritBreakdown?.questionMerit || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Answers Merit */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 flex items-center">
                      <div className="bg-indigo-100 dark:bg-indigo-700 p-2 rounded-full mr-3">
                        <MessageCircle className="h-5 w-5 text-indigo-600 dark:text-indigo-300" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Answers</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {profileData.meritBreakdown?.answerMerit || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Accepted Answers Merit */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 flex items-center">
                      <div className="bg-amber-100 dark:bg-amber-700 p-2 rounded-full mr-3">
                        <CheckCircle className="h-5 w-5 text-amber-600 dark:text-amber-300" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Accepted Answers</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {profileData.meritBreakdown?.acceptedAnswerMerit || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              
              {/* Recent materials */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Recent Materials
                  </h3>
                  <button
                    onClick={() => setActiveTab('materials')}
                    className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-500 flex items-center"
                  >
                    View all <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
                
                {profileData.recentMaterials.length > 0 ? (
                  <div className="space-y-3">
                    {profileData.recentMaterials.map((material) => (
                      <div 
                        key={material.id} 
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
                      >
                        <Link 
                          to={`/materials/${material.id}`}
                          className="text-base font-medium text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400"
                        >
                          {material.title}
                        </Link>
                        <div className="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                          <span className="truncate">{material.courseCode}: {material.courseName}</span>
                          <span className="mx-2">•</span>
                          <span>{new Date(material.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center mt-2 space-x-4">
                          <div className="flex items-center text-green-600 dark:text-green-400">
                            <ArrowUp className="h-4 w-4 mr-1" />
                            <span className="text-sm">{material.upvotes}</span>
                          </div>
                          <div className="flex items-center text-red-600 dark:text-red-400">
                            <ArrowDown className="h-4 w-4 mr-1" />
                            <span className="text-sm">{material.downvotes}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">
                    No materials yet.
                  </p>
                )}
              </div>
              
              {/* Recent comments */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Recent Comments
                  </h3>
                  <button
                    onClick={() => setActiveTab('comments')}
                    className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-500 flex items-center"
                  >
                    View all <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
                
                {profileData.recentComments.length > 0 ? (
                  <div className="space-y-3">
                    {profileData.recentComments.map((comment) => (
                      <div 
                        key={comment.id} 
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
                      >
                        <div className="text-sm text-gray-900 dark:text-white">
                          {comment.content}
                        </div>
                        <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <Link 
                            to={`/materials/${comment.materialId}`}
                            className="truncate hover:text-red-600 dark:hover:text-red-400"
                          >
                            on: {comment.materialTitle}
                          </Link>
                          <span className="mx-2">•</span>
                          <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">
                    No comments yet.
                  </p>
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'materials' && (
            <MaterialsTabContent userId={userId} />
          )}
          
          {activeTab === 'comments' && (
            <CommentsTabContent userId={userId} />
          )}

          {activeTab === 'questions' && (
            <QuestionsTabContent userId={userId} />
          )}

          {activeTab === 'answers' && (
            <AnswersTabContent userId={userId} />
          )}
          
          {activeTab === 'badges' && (
            <div className="py-8">
              <div className="text-center">
                <Award className="h-12 w-12 text-gray-400 mx-auto" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No badges yet</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Badges will be coming soon. Stay tuned!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
              onClick={() => !isReporting && setShowReportModal(false)}
            />
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900">
                  <Flag className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Report User
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Please provide a reason for reporting {profileData.name}. This will be reviewed by moderators.
                    </p>
                  </div>
                  <div className="mt-4">
                    <textarea
                      value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={4}
                      placeholder="Describe why you are reporting this user..."
                      disabled={isReporting}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleReportUser}
                  disabled={isReporting || !reportReason.trim()}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    isReporting || !reportReason.trim()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {isReporting ? 'Reporting...' : 'Submit Report'}
                </button>
                <button
                  type="button"
                  disabled={isReporting}
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfilePage;